import { LIST_TOKENS } from "@src/constants/tokens"
import { HL_V1_MARKETS } from "@src/features/trade/hyperliquid/constants/config"
import type { HLMarketName } from "@src/features/trade/hyperliquid/constants/config"
import { getMarketSymbolFromName } from "@src/features/trade/hyperliquid/marketIdentity"
import type { HLMarketInfo } from "@src/features/trade/hyperliquid/types"

const TOKEN_METADATA_BY_SYMBOL = new Map(
  LIST_TOKENS.map((token) => [
    token.symbol.toUpperCase(),
    { name: token.name, iconUrl: token.icon },
  ])
)

export type MarketMetadata = {
  name: string
  iconUrl?: string
}

export const MARKET_METADATA: Record<string, MarketMetadata> = {
  HYPE: {
    name: "Hyperliquid",
    iconUrl: "/static/icons/network/hyperliquid.svg",
  },
  ONDO: { name: "Ondo", iconUrl: "/static/icons/token/ondo.svg" },
  LIT: { name: "Lighter", iconUrl: "/static/icons/token/lit.svg" },
  WLD: { name: "Worldcoin", iconUrl: "/static/icons/token/wld.svg" },
  ENA: { name: "Ethena", iconUrl: "/static/icons/token/ena.svg" },
  TAO: { name: "Bittensor", iconUrl: "/static/icons/token/tao.svg" },
  XMR: { name: "Monero", iconUrl: "/static/icons/token/xmr.svg" },
  FARTCOIN: { name: "Fartcoin", iconUrl: "/static/icons/token/fartcoin.jpeg" },
  PUMP: { name: "Pump.fun", iconUrl: "/static/icons/token/pump.svg" },
  FET: {
    name: "Artificial Superintelligence",
    iconUrl: "/static/icons/token/fet.svg",
  },
  ICP: { name: "Internet Computer", iconUrl: "/static/icons/token/icp.svg" },
  FIL: { name: "Filecoin", iconUrl: "/static/icons/token/fil.svg" },
  RENDER: { name: "Render", iconUrl: "/static/icons/token/render.svg" },
  KPEPE: { name: "kPEPE", iconUrl: "/static/icons/token/pepe.jpg" },
  ZRO: { name: "LayerZero", iconUrl: "/static/icons/token/zro.svg" },
  JTO: { name: "Jito", iconUrl: "/static/icons/token/jto.svg" },
  PURR: { name: "Purr", iconUrl: "/static/icons/token/purr.jpg" },
  JUP: { name: "Jupiter", iconUrl: "/static/icons/token/jup.svg" },
  GOAT: { name: "Goatseus Maximus", iconUrl: "/static/icons/token/goat.svg" },
  GRASS: { name: "Grass", iconUrl: "/static/icons/token/grass.svg" },
  AR: { name: "Arweave", iconUrl: "/static/icons/token/ar.svg" },
  CRV: { name: "Curve", iconUrl: "/static/icons/token/crv.jpg" },
  INJ: { name: "Injective", iconUrl: "/static/icons/token/inj.svg" },
  VIRTUAL: {
    name: "Virtuals Protocol",
    iconUrl: "/static/icons/token/virtual.svg",
  },
  MEGA: { name: "MegaETH", iconUrl: "/static/icons/token/mega.svg" },
  PENDLE: { name: "Pendle", iconUrl: "/static/icons/token/pendle.svg" },
  PAXG: { name: "PAX Gold", iconUrl: "/static/icons/token/paxg.svg" },
  CHIP: { name: "USD.AI", iconUrl: "/static/icons/token/chip.svg" },
  WLFI: {
    name: "World Liberty Financial",
    iconUrl: "/static/icons/token/wlfi.svg",
  },
  BIO: { name: "Bio Protocol", iconUrl: "/static/icons/token/bio.svg" },
  LDO: { name: "Lido DAO", iconUrl: "/static/icons/token/ldo.svg" },
  HBAR: { name: "Hedera", iconUrl: "/static/icons/token/hbar.svg" },
  DOT: { name: "Polkadot", iconUrl: "/static/icons/token/dot.svg" },
  ETHFI: { name: "Ether.fi", iconUrl: "/static/icons/token/ethfi.svg" },
  MORPHO: { name: "Morpho", iconUrl: "/static/icons/token/morpho.svg" },
  SEI: { name: "Sei", iconUrl: "/static/icons/token/sei.svg" },
  ALGO: { name: "Algorand", iconUrl: "/static/icons/token/algo.svg" },
  GMT: { name: "GMT", iconUrl: "/static/icons/token/gmt.jpg" },
  AERO: { name: "Aerodrome Finance", iconUrl: "/static/icons/token/aero.svg" },
  ZORA: { name: "Zora", iconUrl: "/static/icons/token/zora.svg" },
  KBONK: { name: "kBONK", iconUrl: "/static/icons/token/bonk.svg" },
  SUSHI: { name: "Sushi", iconUrl: "/static/icons/token/sushi.svg" },
  EIGEN: { name: "EigenCloud", iconUrl: "/static/icons/token/eigen.svg" },
  TIA: { name: "Celestia", iconUrl: "/static/icons/token/tia.svg" },
  SKY: { name: "Sky", iconUrl: "/static/icons/token/sky.svg" },
  KSHIB: { name: "Shiba Inu", iconUrl: "/static/icons/token/shib.svg" },
  KNEIRO: { name: "NEIRO", iconUrl: "/static/icons/token/neiro.svg" },
  KLUNC: { name: "LUNC", iconUrl: "/static/icons/token/lunc.svg" },
  CC: { name: "Canton", iconUrl: "/static/icons/token/cc.svg" },
  IO: { name: "io.net", iconUrl: "/static/icons/token/io.svg" },
  IP: { name: "Story", iconUrl: "/static/icons/token/ip.svg" },
  AZTEC: { name: "Aztec", iconUrl: "/static/icons/token/aztec.svg" },
  W: { name: "Wormhole", iconUrl: "/static/icons/token/w.svg" },
  APE: { name: "ApeCoin", iconUrl: "/static/icons/token/ape.svg" },
  DYDX: { name: "dYdX", iconUrl: "/static/icons/token/dydx.svg" },
  ATOM: { name: "Cosmos Hub", iconUrl: "/static/icons/token/atom.svg" },
  NIL: { name: "Nillion", iconUrl: "/static/icons/token/nil.svg" },
  VINE: { name: "Vine", iconUrl: "/static/icons/token/vine.svg" },
  KAS: { name: "Kaspa", iconUrl: "/static/icons/token/kas.svg" },
  AVNT: { name: "Avantis", iconUrl: "/static/icons/token/avnt.svg" },
  MEME: { name: "Memecoin", iconUrl: "/static/icons/token/meme.svg" },
  POPCAT: { name: "Popcat", iconUrl: "/static/icons/token/popcat.svg" },
  MNT: { name: "Mantle", iconUrl: "/static/icons/token/mnt.svg" },
  LINEA: { name: "Linea", iconUrl: "/static/icons/token/linea.svg" },
  PYTH: { name: "Pyth Network", iconUrl: "/static/icons/token/pyth.svg" },
  HMSTR: { name: "Hamster Kombat", iconUrl: "/static/icons/token/hmstr.jpg" },
  CAKE: { name: "PancakeSwap", iconUrl: "/static/icons/token/cake.svg" },
  STABLE: { name: "Stable", iconUrl: "/static/icons/token/stable.svg" },
  PNUT: {
    name: "Peanut the Squirrel",
    iconUrl: "/static/icons/token/pnut.jpg",
  },
  ORDI: { name: "ORDI", iconUrl: "/static/icons/token/ordi.svg" },
  SYRUP: { name: "Maple Finance", iconUrl: "/static/icons/token/syrup.svg" },
  LAYER: { name: "Solayer", iconUrl: "/static/icons/token/layer.svg" },
  ETC: { name: "Ethereum Classic", iconUrl: "/static/icons/token/etc.svg" },
  ZK: { name: "ZKsync", iconUrl: "/static/icons/token/zk.svg" },
  AIXBT: { name: "aixbt", iconUrl: "/static/icons/token/aixbt.svg" },
  STX: { name: "Stacks", iconUrl: "/static/icons/token/stx.svg" },
  NXPC: { name: "Nexpace", iconUrl: "/static/icons/token/nxpc.svg" },
  "0G": { name: "0G", iconUrl: "/static/icons/token/0g.svg" },
  INIT: { name: "Initia", iconUrl: "/static/icons/token/init.svg" },
  ZEN: { name: "Horizen", iconUrl: "/static/icons/token/zen.svg" },
  ALT: { name: "AltLayer", iconUrl: "/static/icons/token/alt.svg" },
  MOVE: { name: "Movement", iconUrl: "/static/icons/token/move.svg" },
  MOODENG: { name: "Moo Deng", iconUrl: "/static/icons/token/moodeng.jpg" },
  S: { name: "Sonic", iconUrl: "/static/icons/token/s.svg" },
  REZ: { name: "Renzo", iconUrl: "/static/icons/token/rez.svg" },
  MERL: { name: "Merlin Chain", iconUrl: "/static/icons/token/merl.svg" },
  YGG: { name: "Yield Guild Games", iconUrl: "/static/icons/token/ygg.svg" },
  SNX: { name: "Synthetix", iconUrl: "/static/icons/token/snx.svg" },
  MANTA: { name: "Manta Network", iconUrl: "/static/icons/token/manta.svg" },
  MET: { name: "Meteora", iconUrl: "/static/icons/token/met.svg" },
  SAGA: { name: "Saga", iconUrl: "/static/icons/token/saga.svg" },
  POLYX: { name: "Polymesh", iconUrl: "/static/icons/token/polyx.svg" },
  RUNE: { name: "THORChain", iconUrl: "/static/icons/token/rune.svg" },
  ANIME: { name: "Animecoin", iconUrl: "/static/icons/token/anime.svg" },
  "2Z": { name: "DoubleZero", iconUrl: "/static/icons/token/2z.svg" },
  FOGO: { name: "Fogo", iconUrl: "/static/icons/token/fogo.svg" },
  APEX: { name: "APEX", iconUrl: "/static/icons/token/apex.svg" },
  COMP: { name: "Compound", iconUrl: "/static/icons/token/comp.svg" },
  CFX: { name: "Conflux", iconUrl: "/static/icons/token/cfx.svg" },
  SUPER: { name: "SuperVerse", iconUrl: "/static/icons/token/super.svg" },
  SAND: { name: "The Sandbox", iconUrl: "/static/icons/token/sand.svg" },
  GRIFFAIN: { name: "GRIFFAIN", iconUrl: "/static/icons/token/griffain.svg" },
  DYM: { name: "Dymension", iconUrl: "/static/icons/token/dym.svg" },
  BLUR: { name: "Blur", iconUrl: "/static/icons/token/blur.svg" },
  STBL: { name: "STBL", iconUrl: "/static/icons/token/stbl.svg" },
  ENS: {
    name: "Ethereum Name Service",
    iconUrl: "/static/icons/token/ens.svg",
  },
  PROVE: { name: "Succinct", iconUrl: "/static/icons/token/prove.svg" },
  AXS: { name: "Axie Infinity", iconUrl: "/static/icons/token/axs.svg" },
  HEMI: { name: "Hemi", iconUrl: "/static/icons/token/hemi.svg" },
  IMX: { name: "Immutable", iconUrl: "/static/icons/token/imx.svg" },
  CELO: { name: "Celo", iconUrl: "/static/icons/token/celo.svg" },
  NOT: { name: "Notcoin", iconUrl: "/static/icons/token/not.svg" },
  HYPER: { name: "Hyperlane", iconUrl: "/static/icons/token/hyper.svg" },
  GALA: { name: "GALA", iconUrl: "/static/icons/token/gala.svg" },
  ME: { name: "Magic Eden", iconUrl: "/static/icons/token/me.svg" },
  IOTA: { name: "IOTA", iconUrl: "/static/icons/token/iota.svg" },
  KFLOKI: { name: "FLOKI", iconUrl: "/static/icons/token/floki.svg" },
  RESOLV: { name: "Resolv", iconUrl: "/static/icons/token/resolv.svg" },
  TRB: { name: "Tellor Tributes", iconUrl: "/static/icons/token/trb.svg" },
  BABY: { name: "Babylon", iconUrl: "/static/icons/token/baby.svg" },
  NEO: { name: "NEO", iconUrl: "/static/icons/token/neo.svg" },
  MINA: { name: "Mina Protocol", iconUrl: "/static/icons/token/mina.svg" },
  SKR: { name: "Seeker", iconUrl: "/static/icons/token/skr.svg" },
  ACE: { name: "Fusionist", iconUrl: "/static/icons/token/ace.svg" },
  UMA: { name: "UMA", iconUrl: "/static/icons/token/uma.svg" },
  GAS: { name: "GAS", iconUrl: "/static/icons/token/gas.svg" },
  BSV: { name: "Bitcoin SV", iconUrl: "/static/icons/token/bsv.svg" },
  BIGTIME: { name: "Big Time", iconUrl: "/static/icons/token/bigtime.svg" },
  ZETA: { name: "ZetaChain", iconUrl: "/static/icons/token/zeta.svg" },
  SOPH: { name: "Sophon", iconUrl: "/static/icons/token/soph.svg" },
  ARK: { name: "ARK", iconUrl: "/static/icons/token/ark.svg" },
  WCT: {
    name: "WalletConnect Token",
    iconUrl: "/static/icons/wallets/wallet-connect.svg",
  },
  BANANA: { name: "Banana Gun", iconUrl: "/static/icons/token/banana.svg" },
  XAI: { name: "Xai", iconUrl: "/static/icons/token/xai.svg" },
  USUAL: { name: "Usual", iconUrl: "/static/icons/token/usual.svg" },
  RSR: { name: "Reserve Rights", iconUrl: "/static/icons/token/rsr.svg" },
  TNSR: { name: "Tensor", iconUrl: "/static/icons/token/tnsr.svg" },
  TSLA: { name: "Tesla", iconUrl: "/static/icons/market/tsla.png" },
  NVDA: { name: "NVIDIA", iconUrl: "/static/icons/market/nvda.png" },
  PLTR: { name: "Palantir", iconUrl: "/static/icons/market/pltr.png" },
  HOOD: { name: "Robinhood", iconUrl: "/static/icons/market/hood.svg" },
  INTC: { name: "Intel", iconUrl: "/static/icons/market/intc.svg" },
  COIN: { name: "Coinbase", iconUrl: "/static/icons/market/coin.svg" },
  META: { name: "Meta", iconUrl: "/static/icons/market/meta.svg" },
  AAPL: { name: "Apple", iconUrl: "/static/icons/market/aapl.svg" },
  MSFT: { name: "Microsoft", iconUrl: "/static/icons/market/msft.svg" },
  ORCL: { name: "Oracle", iconUrl: "/static/icons/market/orcl.svg" },
  GOOGL: { name: "Alphabet", iconUrl: "/static/icons/market/googl.svg" },
  AMZN: { name: "Amazon", iconUrl: "/static/icons/market/amzn.svg" },
  AMD: { name: "AMD", iconUrl: "/static/icons/market/amd.svg" },
  MU: { name: "Micron", iconUrl: "/static/icons/market/mu.svg" },
  SNDK: { name: "SanDisk", iconUrl: "/static/icons/market/sndk.svg" },
  MSTR: { name: "Strategy", iconUrl: "/static/icons/market/mstr.svg" },
  CRCL: { name: "Circle", iconUrl: "/static/icons/market/crcl.svg" },
  NFLX: { name: "Netflix", iconUrl: "/static/icons/market/nflx.svg" },
  COST: { name: "Costco", iconUrl: "/static/icons/market/cost.svg" },
  LLY: { name: "Eli Lilly", iconUrl: "/static/icons/market/lly.svg" },
  TSM: { name: "TSMC", iconUrl: "/static/icons/market/tsm.svg" },
  RIVN: { name: "Rivian", iconUrl: "/static/icons/market/rivn.svg" },
  BABA: { name: "Alibaba", iconUrl: "/static/icons/market/baba.svg" },
  GME: { name: "GameStop", iconUrl: "/static/icons/market/gme.svg" },
  HIMS: { name: "Hims & Hers", iconUrl: "/static/icons/market/hims.svg" },
  DKNG: { name: "DraftKings", iconUrl: "/static/icons/market/dkng.svg" },
  LITE: { name: "Lumentum", iconUrl: "/static/icons/market/lite.svg" },
  BX: { name: "Blackstone", iconUrl: "/static/icons/market/bx.svg" },
  MRVL: { name: "Marvell", iconUrl: "/static/icons/market/mrvl.svg" },
  RKLB: { name: "Rocket Lab", iconUrl: "/static/icons/market/rklb.svg" },
  ZM: { name: "Zoom", iconUrl: "/static/icons/market/zm.svg" },
  EBAY: { name: "eBay", iconUrl: "/static/icons/market/ebay.svg" },
  ARM: { name: "Arm Holdings", iconUrl: "/static/icons/market/arm.svg" },
  ASML: { name: "ASML", iconUrl: "/static/icons/market/asml.svg" },
  BB: { name: "BlackBerry", iconUrl: "/static/icons/market/bb.svg" },
  DELL: { name: "Dell", iconUrl: "/static/icons/market/dell.svg" },
  IBM: { name: "IBM", iconUrl: "/static/icons/market/ibm.svg" },
  AVGO: { name: "Broadcom", iconUrl: "/static/icons/market/avgo.svg" },
  NOW: { name: "ServiceNow", iconUrl: "/static/icons/market/now.svg" },
  NBIS: { name: "Nebius", iconUrl: "/static/icons/market/nbis.svg" },
  WDC: { name: "Western Digital", iconUrl: "/static/icons/market/wdc.svg" },
  USAR: { name: "USA Rare Earth", iconUrl: "/static/icons/market/usar.svg" },
  CRWV: { name: "CoreWeave", iconUrl: "/static/icons/market/crwv.svg" },
  HYUNDAI: { name: "Hyundai", iconUrl: "/static/icons/market/hyundai.svg" },
  SMSN: { name: "Samsung", iconUrl: "/static/icons/market/samsung.svg" },
  SKHX: { name: "SK Hynix", iconUrl: "/static/icons/market/sk-hynix.svg" },
  SPCX: { name: "SpaceX", iconUrl: "/static/icons/market/spcx.svg" },
  MINIMAX: { name: "MiniMax", iconUrl: "/static/icons/market/minimax.svg" },
  PURRDAT: { name: "PURRDAT", iconUrl: "/static/icons/market/purrdat.svg" },
  BIRD: { name: "BIRD", iconUrl: "/static/icons/market/bird.svg" },
  CBRS: { name: "CBRS", iconUrl: "/static/icons/market/cbrs.svg" },
  QNT: { name: "QNT", iconUrl: "/static/icons/market/qnt.svg" },
  DRAM: { name: "DRAM", iconUrl: "/static/icons/market/dram.svg" },
  XYZ100: { name: "XYZ 100", iconUrl: "/static/icons/market/xyz100.png" },
  SP500: { name: "S&P 500", iconUrl: "/static/icons/market/sp500.svg" },
  JP225: { name: "Nikkei 225", iconUrl: "/static/icons/market/jp225.svg" },
  KR200: { name: "KOSPI 200", iconUrl: "/static/icons/market/kr200.svg" },
  SMH: {
    name: "VanEck Semiconductor ETF",
    iconUrl: "/static/icons/market/smh.svg",
  },
  XLE: {
    name: "Energy Select Sector SPDR",
    iconUrl: "/static/icons/market/xle.svg",
  },
  EWY: {
    name: "iShares MSCI South Korea ETF",
    iconUrl: "/static/icons/market/ewy.svg",
  },
  EWJ: {
    name: "iShares MSCI Japan ETF",
    iconUrl: "/static/icons/market/ewj.svg",
  },
  EWZ: {
    name: "iShares MSCI Brazil ETF",
    iconUrl: "/static/icons/market/ewz.svg",
  },
  EWT: {
    name: "iShares MSCI Taiwan ETF",
    iconUrl: "/static/icons/market/ewt.svg",
  },
  URNM: {
    name: "Sprott Uranium Miners ETF",
    iconUrl: "/static/icons/market/uranium.svg",
  },
  GOLD: { name: "Gold", iconUrl: "/static/icons/market/gold.svg" },
  SILVER: { name: "Silver", iconUrl: "/static/icons/market/silver.svg" },
  COPPER: { name: "Copper", iconUrl: "/static/icons/market/copper.svg" },
  NATGAS: { name: "Natural Gas", iconUrl: "/static/icons/market/natgas.svg" },
  CL: { name: "WTI Crude Oil", iconUrl: "/static/icons/market/oil.svg" },
  BRENTOIL: {
    name: "Brent Crude Oil",
    iconUrl: "/static/icons/market/oil.svg",
  },
  PLATINUM: { name: "Platinum", iconUrl: "/static/icons/market/platinum.svg" },
  PALLADIUM: {
    name: "Palladium",
    iconUrl: "/static/icons/market/palladium.svg",
  },
  JPY: { name: "Japanese Yen", iconUrl: "/static/icons/market/jpy.svg" },
  EUR: { name: "Euro", iconUrl: "/static/icons/market/eur.svg" },
  GBP: { name: "British Pound", iconUrl: "/static/icons/market/gbp.svg" },
}

export function isSupportedMarket(coin: string): coin is HLMarketName {
  return (HL_V1_MARKETS as readonly string[]).includes(coin)
}

export function getMarketMetadata(
  coinOrMarket: string | Pick<HLMarketInfo, "displayName" | "name">
): MarketMetadata {
  if (typeof coinOrMarket !== "string") {
    const fallback = getMarketMetadata(coinOrMarket.name)
    return { name: coinOrMarket.displayName, iconUrl: fallback.iconUrl }
  }

  const coin = coinOrMarket
  const symbol = getMarketSymbolFromName(coin).toUpperCase()
  const metadata = MARKET_METADATA[symbol]
  if (metadata) return metadata

  const tokenMetadata = TOKEN_METADATA_BY_SYMBOL.get(symbol)
  if (tokenMetadata) return tokenMetadata

  return { name: getMarketSymbolFromName(coin) }
}
